import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  InputAdornment,
  Pagination,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  ArrowForward as ArrowIcon,
  Clear as ClearIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import eventService from '../../services/eventService';
import { CardSkeleton } from '../../components/common/LoadingSkeleton';
import { EmptySearch } from '../../components/common/EmptyState';
import { toast } from '../../components/common/ToastProvider';

const EventListing = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalEvents: 0,
    limit: 9,
  });

  // Filters
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  // Fetch published events
  const fetchEvents = async (page = 1) => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: pagination.limit,
        status: 'published', // Only show published events
        ...(search && { search }),
        ...(category && { category }),
      };

      const response = await eventService.getEvents(params);

      if (response.success) {
        setEvents(response.data.events);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch events';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setCategory('');
    fetchEvents(1);
  };

  const hasActiveFilters = search || category;

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchEvents(1);
  };

  const handlePageChange = (event, value) => {
    fetchEvents(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const getSpotsRemaining = (event) => {
    if (!event.capacity || event.capacity === 0) return null;
    const remaining = event.capacity - (event.totalRSVPs || 0);
    return remaining > 0 ? remaining : 0;
  };

  const isEventFull = (event) => {
    if (!event.capacity || event.capacity === 0) return false;
    return event.totalRSVPs >= event.capacity;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 20px rgba(102, 126, 234, 0.1)',
              }}
            >
              Discover Events
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.5rem' },
                lineHeight: 1.6,
                maxWidth: '700px',
                mx: 'auto',
                fontWeight: 400,
              }}
            >
              Find and join exciting events happening around you
            </Typography>
          </motion.div>
        </Box>

        {/* Filters - Glassmorphic */}
        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 4,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.5)',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  placeholder="Search events by name or description..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                      },
                      '&.Mui-focused': {
                        bgcolor: 'white',
                        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="General">General</MenuItem>
                  <MenuItem value="Conference">Conference</MenuItem>
                  <MenuItem value="Workshop">Workshop</MenuItem>
                  <MenuItem value="Seminar">Seminar</MenuItem>
                  <MenuItem value="Meetup">Meetup</MenuItem>
                  <MenuItem value="Social">Social</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  sx={{
                    height: '56px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                    },
                  }}
                >
                  Search Events
                </Button>
              </Box>
            </Box>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      borderColor: 'primary.dark',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* Event Count */}
        {!loading && events.length > 0 && (
          <Box mb={3}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500, fontSize: '1rem' }}
            >
              Showing {events.length} of {pagination.totalEvents} events
            </Typography>
          </Box>
        )}

        {/* Loading Skeleton */}
        {loading ? (
          <CardSkeleton count={9} />
        ) : events.length === 0 ? (
          /* Empty State */
          <EmptySearch
            searchTerm={search || category}
            onClear={hasActiveFilters ? handleClearFilters : undefined}
          />
        ) : (
          <>
            {/* Event Grid with Glassmorphic Cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                },
                gap: 3,
              }}
            >
              {events.map((event, index) => (
                <motion.div
                  key={event._id}
                  initial={{ opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.15)',
                          borderColor: 'rgba(102, 126, 234, 0.5)',
                          background: 'rgba(255, 255, 255, 0.9)',
                          '& .event-image': {
                            transform: 'scale(1.05)',
                          },
                        },
                      }}
                      onClick={() => navigate(`/events/${event.slug || event._id}`)}
                    >
                      {/* Image with Gradient Overlay */}
                      {event.banner?.url && (
                        <Box
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            height: 240,
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={eventService.getImageUrl(event.banner.url)}
                            alt={event.eventName}
                            className="event-image"
                            sx={{
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease',
                            }}
                          />
                          {/* Gradient Overlay */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '50%',
                              background:
                                'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)',
                            }}
                          />
                        </Box>
                      )}

                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        {/* Category and Spots */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap">
                          <Chip
                            label={event.category}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(102, 126, 234, 0.1)',
                              color: 'primary.main',
                              border: '1px solid',
                              borderColor: 'rgba(102, 126, 234, 0.3)',
                              fontWeight: 600,
                              fontSize: '0.8125rem',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.2)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          />
                          {event.capacity > 0 && (
                            <Chip
                              label={
                                isEventFull(event)
                                  ? 'Full'
                                  : `${getSpotsRemaining(event)} spots`
                              }
                              size="small"
                              sx={{
                                bgcolor: isEventFull(event)
                                  ? 'rgba(244, 67, 54, 0.1)'
                                  : getSpotsRemaining(event) <= 5
                                  ? 'rgba(255, 152, 0, 0.1)'
                                  : 'rgba(76, 175, 80, 0.1)',
                                color: isEventFull(event)
                                  ? 'error.main'
                                  : getSpotsRemaining(event) <= 5
                                  ? 'warning.main'
                                  : 'success.main',
                                border: '1px solid',
                                borderColor: isEventFull(event)
                                  ? 'rgba(244, 67, 54, 0.3)'
                                  : getSpotsRemaining(event) <= 5
                                  ? 'rgba(255, 152, 0, 0.3)'
                                  : 'rgba(76, 175, 80, 0.3)',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                              }}
                            />
                          )}
                        </Box>

                        {/* Event Name */}
                        <Typography
                          variant="h6"
                          component="h2"
                          gutterBottom
                          sx={{
                            fontWeight: 700,
                            fontSize: '1.25rem',
                            lineHeight: 1.3,
                            mb: 1.5,
                          }}
                        >
                          {event.eventName}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.6,
                            fontSize: '0.9375rem',
                          }}
                        >
                          {event.description}
                        </Typography>

                        {/* Event Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {/* Date & Time */}
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                flexShrink: 0,
                              }}
                            >
                              <CalendarIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {event.dates?.[0]?.startDate && formatDate(event.dates[0].startDate)}
                              {event.dates?.[0]?.startTime &&
                                ` at ${formatTime(event.dates[0].startTime)}`}
                            </Typography>
                          </Box>

                          {/* Location */}
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                flexShrink: 0,
                              }}
                            >
                              <LocationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {event.location?.venue}
                            </Typography>
                          </Box>

                          {/* Attendees */}
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                flexShrink: 0,
                              }}
                            >
                              <PeopleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {event.totalRSVPs || 0} {event.totalRSVPs === 1 ? 'person' : 'people'} attending
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>

                      {/* Action Button */}
                      <CardActions sx={{ p: 3, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          endIcon={<ArrowIcon />}
                          disabled={isEventFull(event)}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/events/${event.slug || event._id}`);
                          }}
                          sx={{
                            background: isEventFull(event)
                              ? 'rgba(0, 0, 0, 0.12)'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontWeight: 600,
                            py: 1.25,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                            },
                            '&:disabled': {
                              background: 'rgba(0, 0, 0, 0.12)',
                              color: 'rgba(0, 0, 0, 0.26)',
                            },
                          }}
                        >
                          {isEventFull(event) ? 'Event Full' : 'View Details'}
                        </Button>
                      </CardActions>
                    </Card>
                  </motion.div>
              ))}
            </Box>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={6}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontWeight: 500,
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      '&:hover': {
                        bgcolor: 'rgba(102, 126, 234, 0.1)',
                        transform: 'scale(1.1)',
                      },
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 700,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default EventListing;
